import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { FaLongArrowAltRight } from 'react-icons/fa';
import { useTimeoutFn } from 'react-use';

import PurchasePage from '../../components/payments/PurchasePage';
import {
  SET_ORDER_DETAILS,
  SET_LOADING_CREATE_PAYMENT_INTENT,
  SET_ERROR_CREATE_PAYMENT_INTENT,
  usePurchasePage,
  SET_IS_SUBSCRIPTION,
  SET_POLLING_SUBSCRIPTION_STATUS,
  SET_ERROR_SUBSCRIPTION_STATUS,
  SET_FETCHING_PRORATION_PREVIEW,
} from '../../hooks/usePurchasePage';
import {
  FAILED_PAYMENT_DETAILS,
  PREVIEW_PRORATION_QUERY,
  SUBSCRIPTION_STATUS,
} from '../../queries';
import CompletePurchaseButton from '../../components/payments/CompletePurchaseButton';
import Button from '../../components/inputs/Button';
import { getSubscriptionPrice } from '../../helpers/pricing';
import { capitalize, currency } from '../../utils';
import { useAuth } from '../../hooks/useAuth';
import { CHANGE_SUBSCRIPTION, DELETE_SUBSCRIPTION } from '../../mutations';
import { isStripePaymentRequired } from '../../utils/featureFlags';

function ProductLabel({ plan }) {
  const { user } = useAuth();
  const { plan: currentPlan } = user;

  const { state } = usePurchasePage();
  const { orderDetails } = state;
  const upgrading = orderDetails?.upgrading;

  return (
    <h4 className="mb-0">
      <div className="flex items-center">
        <span className="mr-2">Subscription:</span>
        {currentPlan !== plan && upgrading && (
          <div className="flex items-center">
            <span className="line-through mr-2 font-normal">
              {capitalize(currentPlan)}
            </span>
            <FaLongArrowAltRight className="mr-2" />
          </div>
        )}
        <span className="font-normal">{capitalize(plan)}</span>
      </div>
    </h4>
  );
}

function ProductPricing({
  plan,
  recurringInterval: incomingRecurringInterval,
}) {
  const { state, dispatch } = usePurchasePage();
  const upgrading = state.orderDetails?.upgrading;
  const retryingFailedPayment = state.orderDetails?.retryingFailedPayment;

  const [previewProration, { loading, error, data }] = useLazyQuery(
    PREVIEW_PRORATION_QUERY
  );
  const prorationPreview = data?.previewProration;

  const [monthlyPrice, quarterlyPrice] = useMemo(() => {
    return [
      getSubscriptionPrice(plan, 'monthly'),
      getSubscriptionPrice(plan, 'quarterly'),
    ];
  }, [plan]);

  const [recurringInterval, setRecurringInterval] = useState(
    () => incomingRecurringInterval
  );

  useEffect(() => {
    if (upgrading) {
      previewProration({
        variables: {
          targetPlan: plan,
          targetRecurringInterval: recurringInterval,
        },
      });
    }
  }, [plan, previewProration, recurringInterval, upgrading]);

  useEffect(() => {
    dispatch({
      type: SET_FETCHING_PRORATION_PREVIEW,
      payload: loading,
    });
  }, [loading, dispatch]);

  useEffect(() => {
    dispatch({
      type: SET_ERROR_CREATE_PAYMENT_INTENT,
      payload: error,
    });
  }, [error, dispatch]);

  useEffect(() => {
    if (prorationPreview?.total) {
      dispatch({
        type: SET_ORDER_DETAILS,
        payload: {
          price: prorationPreview.total?.amount,
          prorationDate: prorationPreview?.prorationDate,
        },
      });
    }
  }, [dispatch, prorationPreview]);

  useEffect(() => {
    if (!upgrading) {
      dispatch({
        type: SET_ORDER_DETAILS,
        payload: {
          price:
            recurringInterval === 'quarterly' ? quarterlyPrice : monthlyPrice,
          recurringInterval,
        },
      });
    }
  }, [dispatch, monthlyPrice, quarterlyPrice, recurringInterval, upgrading]);

  return (
    <div>
      <div>
        <h4>Billing Cycle</h4>
        <div
          className={`flex ${
            retryingFailedPayment ? 'opacity-70 select-none cursor-default' : ''
          }`}
        >
          <button
            type="button"
            onClick={() => setRecurringInterval('monthly')}
            disabled={retryingFailedPayment}
            className={`text-lg text-white px-8 min-w-52 py-2 font-extrabold rounded-l-full border-t-3 border-b-3 border-l-3 ${
              recurringInterval === 'monthly'
                ? 'bg-secondary border-secondary hover:shadow'
                : 'bg-gray-100 border-gray-300 text-gray-500'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setRecurringInterval('quarterly')}
            disabled={retryingFailedPayment}
            className={`relative text-lg text-white px-8 min-w-52 py-2 font-extrabold rounded-r-full border-t-3 border-b-3 border-r-3 ${
              recurringInterval === 'quarterly'
                ? 'bg-secondary border-secondary hover:shadow'
                : 'bg-gray-100 border-gray-300 text-gray-500'
            }`}
          >
            <div className="flex items-center">
              <div>Quarterly </div>
              <div
                className={`text-sm ${
                  recurringInterval === 'quarterly'
                    ? 'text-white'
                    : 'text-secondary'
                } font-semibold whitespace-nowrap ml-3 mt-0.5`}
              >
                Save 20%
              </div>
            </div>
            {recurringInterval !== 'quarterly' && !retryingFailedPayment && (
              <span className="absolute top-0 right-0 flex h-3 w-3">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary"></span>
              </span>
            )}
          </button>
        </div>
      </div>
      {upgrading && (
        <div className="mt-8">
          {prorationPreview?.cost && (
            <div className="mb-4">
              <p className="-mb-1">
                Cost of new plan:{' '}
                <span className="font-bold ml-2">
                  {currency(prorationPreview.cost?.amount)}
                </span>
              </p>
              <span className="text-sm text-gray-600">
                {prorationPreview.cost?.description}
              </span>
            </div>
          )}
          {prorationPreview?.credit && (
            <>
              <p className="-mb-1">
                Credit from existing plan:{' '}
                <span className="font-bold ml-2">
                  {currency(prorationPreview.credit?.amount)}
                </span>
              </p>
              <span className="text-sm text-gray-600">
                {prorationPreview.credit?.description}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProductCompletePurchase({ plan }) {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { getUser } = useAuth();

  const { state, dispatch } = usePurchasePage();
  const { orderDetails, paymentMethod, promoCodeApplied } = state;
  const recurringInterval = orderDetails?.recurringInterval;
  const prorationDate = orderDetails?.prorationDate;

  const [subscriptionPollingTimeout, setSubscriptionPollingTimeout] =
    useState(null);

  const [changeSubscription, { loading, error, called, data }] =
    useMutation(CHANGE_SUBSCRIPTION);

  useEffect(() => {
    dispatch({
      type: SET_LOADING_CREATE_PAYMENT_INTENT,
      payload: loading,
    });
  }, [loading, dispatch]);

  useEffect(() => {
    let displayMessage = error?.message;

    if (error?.message?.includes('payment-failed:')) {
      displayMessage =
        error.message?.split('payment-failed: ')[1] ||
        'Payment failed. Please try a different payment method.';
    }

    dispatch({
      type: SET_ERROR_CREATE_PAYMENT_INTENT,
      payload: { message: displayMessage },
    });
  }, [error, dispatch]);

  const [
    checkSubscriptionStatus,
    {
      error: errorCheckSubscriptionStatus,
      data: dataCheckSubscriptionStatus,
      stopPolling,
      startPolling,
    },
  ] = useLazyQuery(SUBSCRIPTION_STATUS, {
    fetchPolicy: 'network-only',
  });
  const subscriptionStatus =
    dataCheckSubscriptionStatus?.subscriptionStatus?.status;

  useTimeoutFn(() => {
    if (subscriptionPollingTimeout != null) {
      stopPolling();
      navigate(`/dashboard/profile?plan=open`);
    }
  }, subscriptionPollingTimeout);

  useEffect(() => {
    if (called && data && !error) {
      checkSubscriptionStatus();
      startPolling(300);
      setSubscriptionPollingTimeout(15000);
      dispatch({
        type: SET_POLLING_SUBSCRIPTION_STATUS,
        payload: true,
      });
    }
  }, [
    called,
    checkSubscriptionStatus,
    data,
    dispatch,
    error,
    navigate,
    startPolling,
  ]);

  useEffect(() => {
    stopPolling();
    dispatch({
      type: SET_ERROR_SUBSCRIPTION_STATUS,
      payload: errorCheckSubscriptionStatus,
    });
  }, [errorCheckSubscriptionStatus, dispatch, stopPolling]);

  useEffect(() => {
    if (subscriptionStatus === 'active' && !errorCheckSubscriptionStatus) {
      async function runEffect() {
        stopPolling();
        await apolloClient.refetchQueries({
          include: ['UserDetailsQuery', 'SubscriptionDetails'],
        });
        await getUser({ force: true });
        navigate(`/dashboard/profile?plan=open`);
      }
      runEffect();
    }
  }, [
    subscriptionStatus,
    dispatch,
    navigate,
    stopPolling,
    apolloClient,
    getUser,
    errorCheckSubscriptionStatus,
  ]);

  return (
    <CompletePurchaseButton
      disabled={
        loading ||
        state.pollingSubscriptionState ||
        (!isStripePaymentRequired() ? false : !state.paymentMethod)
      }
      onClick={() =>
        changeSubscription({
          variables: {
            plan,
            recurringInterval,
            pmId: paymentMethod?.id || 'bypass',
            prorationDate,
            promoCode: promoCodeApplied ? promoCodeApplied.code : undefined,
          },
        })
      }
    />
  );
}

function BackButton({ from }) {
  const navigate = useNavigate();
  const { state } = usePurchasePage();
  const { pollingSubscriptionState, loadingCreatePaymentIntent, orderDetails } =
    state;
  const failedPaymentSubscriptionStatus =
    orderDetails?.failedPaymentSubscriptionStatus;

  const [deleteSubscription] = useMutation(DELETE_SUBSCRIPTION);

  function handleClickCancel() {
    deleteSubscription({
      onCompleted: () => {
        navigate(-1);
      },
    });
  }

  if (failedPaymentSubscriptionStatus === 'incomplete') {
    return (
      <Button type="button" secondary small onClick={handleClickCancel}>
        Cancel
      </Button>
    );
  }

  return (
    <Button
      type="button"
      secondary
      small
      onClick={() => {
        if (!from) {
          navigate('../../../profile?plan=open');
          return;
        }
        navigate(from);
      }}
      disabled={pollingSubscriptionState || loadingCreatePaymentIntent}
    >
      Back
    </Button>
  );
}

export default function PurchaseSubscription() {
  const { state, dispatch } = usePurchasePage();
  const pollingSubscriptionStatus = state?.pollingSubscriptionStatus;

  const { user } = useAuth();

  const location = useLocation();
  const from = location.state?.from?.pathname;

  const orderDetails = useMemo(() => {
    let orderDetails = location.state?.orderDetails;
    if (!orderDetails) {
      const params = new URLSearchParams(window.location.search);
      return {
        plan: params.get('plan'),
        recurringInterval: params.get('recurringInterval'),
        upgrading: params.get('upgrading') === 'true' ? true : false,
        retryingFailedPayment:
          params.get('retryingFailedPayment') === 'true' ? true : false,
      };
    }
    return orderDetails;
  }, [location.state]);

  const { plan, recurringInterval, retryingFailedPayment } = orderDetails;

  const [getFailedPaymentDetails, { data }] = useLazyQuery(
    FAILED_PAYMENT_DETAILS
  );
  const failedPaymentDetails = data?.failedPaymentDetails;

  useEffect(() => {
    if (retryingFailedPayment) {
      getFailedPaymentDetails();
    }
  }, [retryingFailedPayment, getFailedPaymentDetails]);

  useEffect(() => {
    if (failedPaymentDetails) {
      dispatch({
        type: SET_ORDER_DETAILS,
        payload: {
          price: failedPaymentDetails.amount,
          failedPaymentSubscriptionStatus: failedPaymentDetails.status,
        },
      });
      dispatch({
        type: SET_ERROR_CREATE_PAYMENT_INTENT,
        payload: {
          message: `${failedPaymentDetails.error} Please try a different payment method or add a new one.`,
        },
      });
    }
  }, [failedPaymentDetails, dispatch]);

  useEffect(() => {
    dispatch({
      type: SET_ORDER_DETAILS,
      payload: orderDetails,
    });
  }, [orderDetails, dispatch]);

  useEffect(() => {
    dispatch({
      type: SET_IS_SUBSCRIPTION,
      payload: true,
    });
  }, [dispatch]);

  if (
    (!plan || !recurringInterval || (user?.plan && user.plan === plan)) &&
    !pollingSubscriptionStatus &&
    !retryingFailedPayment
  ) {
    return <Navigate to={-1} />;
  }

  return (
    <PurchasePage
      productLabel={<ProductLabel plan={plan} />}
      productPricing={
        <ProductPricing plan={plan} recurringInterval={recurringInterval} />
      }
      productCompletePurchase={<ProductCompletePurchase plan={plan} />}
      backButton={<BackButton from={from} />}
    />
  );
}
